import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import MediaDropzone from "@/components/admin/MediaDropzone";

export default function AdminFounder() {
  const [founder, setFounder] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/founder").then((r) => setFounder(r.data?.data || {})).catch(() => {});
  }, []);

  const update = (patch) => setFounder((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/founder", { data: founder });
      toast.success("Founder details saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!founder) return <div className="text-charcoal/40">Loading…</div>;

  return (
    <div data-testid="admin-founder-page">
      <header className="mb-8">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">About</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Founder</h1>
        <p className="text-charcoal/65 font-light mt-2">Upload the founder photo and edit the personal note shown on the homepage.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl">
        <section className="bg-white border border-beige p-5 md:p-6">
          <MediaDropzone
            folder="mostash/founder"
            accept="image"
            height="aspect-[4/5]"
            label="Founder photo"
            testid="founder-photo-dropzone"
            value={founder.image_url ? { url: founder.image_url, public_id: founder.image_public_id, resource_type: "image" } : null}
            onChange={(asset) => {
              if (asset) update({ image_url: asset.url, image_public_id: asset.public_id });
              else update({ image_url: "", image_public_id: "" });
            }}
          />
        </section>

        <section className="bg-white border border-beige p-5 md:p-6 space-y-4">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Eyebrow label</span>
            <input className="mt-2 field-input" value={founder.label || ""} onChange={(e) => update({ label: e.target.value })} data-testid="founder-label" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Headline</span>
            <input className="mt-2 field-input" value={founder.headline || ""} onChange={(e) => update({ headline: e.target.value })} data-testid="founder-headline-input" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Name</span>
            <input className="mt-2 field-input" value={founder.name || ""} onChange={(e) => update({ name: e.target.value })} data-testid="founder-name" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Founder note (one paragraph per line)</span>
            <textarea
              rows={8}
              className="mt-2 field-textarea"
              value={(founder.paragraphs || []).join("\n\n")}
              onChange={(e) => update({ paragraphs: e.target.value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean) })}
              data-testid="founder-paragraphs"
            />
            <span className="text-[11px] text-charcoal/45 mt-1 block">Separate paragraphs with a blank line.</span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">CTA label</span>
              <input className="mt-2 field-input" value={founder.cta?.label || ""} onChange={(e) => update({ cta: { ...(founder.cta || {}), label: e.target.value } })} data-testid="founder-cta-label" />
            </label>
            <label className="block">
              <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">CTA link</span>
              <input className="mt-2 field-input" value={founder.cta?.href || ""} onChange={(e) => update({ cta: { ...(founder.cta || {}), href: e.target.value } })} data-testid="founder-cta-href" />
            </label>
          </div>
        </section>
      </div>

      <div className="mt-6">
        <button type="button" onClick={save} disabled={saving} data-testid="founder-save" className="btn-primary h-11 px-6 text-[11px] disabled:opacity-60">
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" />Saving</> : <><Save className="h-4 w-4" /> Save changes</>}
        </button>
      </div>
    </div>
  );
}
